import { addDays, compare, daysBetween, diffYMD, type YMD } from "./dates";

export interface JobSpan { start: YMD; end: YMD }
export interface Duration { years: number; months: number; days: number; totalDays: number }

/** The end date as an exclusive bound: with "count the end date" a job from 1 Jan to 31 Dec is a full year. */
const endBound = (end: YMD, inclusive: boolean) => (inclusive ? addDays(end, 1) : end);

export function jobDuration(span: JobSpan, inclusive: boolean): Duration | null {
  // An end date before the start date is never a job, even when "count the end date" would add a day back (B-0151).
  if (compare(span.end, span.start) < 0) return null;
  const to = endBound(span.end, inclusive);
  return { ...diffYMD(span.start, to), totalDays: daysBetween(span.start, to) };
}

/**
 * Total experience across jobs: overlapping (or touching) periods are merged first so nothing is counted twice,
 * then each merged period's calendar duration is added up (months and days carried, with 30-day months for the
 * carry only).
 */
export function totalExperience(spans: JobSpan[], inclusive: boolean): (Duration & { periods: number }) | null {
  const iv = spans
    .filter((s) => compare(s.end, s.start) >= 0)
    .map((s) => ({ start: s.start, end: endBound(s.end, inclusive) }))
    .filter((s) => compare(s.end, s.start) >= 0)
    .sort((a, b) => compare(a.start, b.start));
  if (!iv.length) return null;
  const merged: { start: YMD; end: YMD }[] = [];
  for (const s of iv) {
    const last = merged[merged.length - 1];
    if (last && compare(s.start, last.end) <= 0) { if (compare(s.end, last.end) > 0) last.end = s.end; }
    else merged.push({ ...s });
  }
  let months = 0, days = 0, totalDays = 0;
  for (const m of merged) {
    const d = diffYMD(m.start, m.end);
    months += d.years * 12 + d.months; days += d.days; totalDays += daysBetween(m.start, m.end);
  }
  months += Math.floor(days / 30); days %= 30;
  return { years: Math.floor(months / 12), months: months % 12, days, totalDays, periods: merged.length };
}

/** "4.6 years" as application forms ask for it. */
export const decimalYears = (totalDays: number) => Math.round((totalDays / 365.25) * 10) / 10;

export interface EmploymentGap {
  /** The last day worked before the gap (the end date of the earlier period). */
  after: YMD;
  /** The first day of the next period of work. */
  before: YMD;
  years: number; months: number; days: number; totalDays: number;
}

/**
 * Time between jobs: the periods are merged exactly as totalExperience merges them (overlaps and touching jobs
 * leave no gap), and each stretch with no job between two merged periods is listed with its length. With
 * "count the end date" the last day worked is not part of the gap, so 30 Jun to 1 Sep is 2 months (July and
 * August). Jobs with an end before their start are ignored.
 */
export function employmentGaps(spans: JobSpan[], inclusive: boolean): EmploymentGap[] {
  const iv = spans
    .filter((s) => compare(s.end, s.start) >= 0)
    .map((s) => ({ start: s.start, end: s.end, bound: endBound(s.end, inclusive) }))
    .sort((a, b) => compare(a.start, b.start));
  const merged: { start: YMD; end: YMD; bound: YMD }[] = [];
  for (const s of iv) {
    const last = merged[merged.length - 1];
    if (last && compare(s.start, last.bound) <= 0) { if (compare(s.bound, last.bound) > 0) { last.bound = s.bound; last.end = s.end; } }
    else merged.push({ ...s });
  }
  const gaps: EmploymentGap[] = [];
  for (let i = 1; i < merged.length; i++) {
    const from = merged[i - 1].bound, to = merged[i].start;
    gaps.push({ after: merged[i - 1].end, before: to, ...diffYMD(from, to), totalDays: daysBetween(from, to) });
  }
  return gaps;
}

export const gapLabel = (g: Pick<EmploymentGap, "years" | "months" | "days">) =>
  [g.years && `${g.years} yr`, g.months && `${g.months} mo`, (g.days || (!g.years && !g.months)) && `${g.days} d`].filter(Boolean).join(" ");
